import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import { useAuth } from '@context';
import { useCourses, useEmailDispatchLogs } from '@hooks';
import { COURSE_EMAIL_TEMPLATE_DEFINITIONS, COURSE_EMAIL_TEMPLATE_TYPES } from '@config/emailTemplates';
import { getManagedCourses, isAdmin } from '@utils/permissions';

const DEFAULT_STATUS_OPTIONS = Object.freeze([
  'pending',
  'sent',
  'simulated',
  'failed',
  'skipped_disabled',
  'skipped_no_recipient',
]);

const DEFAULT_PROVIDER_OPTIONS = Object.freeze(['log', 'webhook', 'resend', 'postmark', 'smtp', 'disabled', 'system']);

const STATUS_VARIANTS = Object.freeze({
  pending: 'secondary',
  sent: 'success',
  simulated: 'info',
  failed: 'danger',
  skipped_disabled: 'warning',
  skipped_no_recipient: 'dark',
});

const PROVIDER_VARIANTS = Object.freeze({
  log: 'info',
  webhook: 'primary',
  resend: 'primary',
  postmark: 'success',
  smtp: 'warning',
  disabled: 'secondary',
  system: 'dark',
});

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const truncate = (value, maxLength = 72) => {
  const normalized = String(value || '');
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 3)}...`;
};

const formatTemplateLabel = (templateType) => {
  if (!templateType) return 'Unknown';
  return COURSE_EMAIL_TEMPLATE_DEFINITIONS[templateType]?.label || templateType;
};

export function EmailLogsPage() {
  const { user } = useAuth();
  const { courses, loading: loadingCourses, error: coursesError } = useCourses();

  const userIsAdmin = isAdmin(user);
  const scopedCourses = useMemo(
    () => getManagedCourses(user, courses),
    [courses, user]
  );

  const [courseId, setCourseId] = useState('');
  const [status, setStatus] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [provider, setProvider] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    if (userIsAdmin) {
      return;
    }

    if (!scopedCourses.length) {
      setCourseId('');
      return;
    }

    if (courseId && scopedCourses.some((course) => course.id === courseId)) {
      return;
    }

    setCourseId(scopedCourses[0].id);
  }, [courseId, scopedCourses, userIsAdmin]);

  useEffect(() => {
    setPage(1);
  }, [courseId, status, templateType, provider, search]);

  const canQueryLogs = userIsAdmin || Boolean(courseId);

  const {
    logs,
    pagination,
    statusOptions,
    providerOptions,
    templateTypes,
    loading,
    error,
    refresh,
    clearError,
  } = useEmailDispatchLogs(
    {
      courseId,
      status,
      templateType,
      provider,
      search,
      page,
      pageSize: 25,
    },
    canQueryLogs
  );

  useEffect(() => {
    if (pagination.page && pagination.page !== page) {
      setPage(pagination.page);
    }
  }, [pagination.page, page]);

  const effectiveStatusOptions = statusOptions.length ? statusOptions : DEFAULT_STATUS_OPTIONS;
  const effectiveProviderOptions = providerOptions.length ? providerOptions : DEFAULT_PROVIDER_OPTIONS;
  const effectiveTemplateTypes = templateTypes.length ? templateTypes : COURSE_EMAIL_TEMPLATE_TYPES;

  const handleClearFilters = () => {
    if (!userIsAdmin && scopedCourses.length) {
      setCourseId(scopedCourses[0].id);
    } else {
      setCourseId('');
    }

    setStatus('');
    setTemplateType('');
    setProvider('');
    setSearch('');
    setPage(1);
  };

  const showManagedCourseMessage = !userIsAdmin && !loadingCourses && scopedCourses.length === 0;

  return (
    <div>
      <div className="mb-4">
        <h4 className="mb-1">Email Dispatch Logs</h4>
        <small className="text-muted">
          Audit student-facing email delivery results, provider responses, and error details.
        </small>
      </div>

      {coursesError && (
        <Alert variant="danger" className="mb-3">
          {coursesError}
        </Alert>
      )}

      {showManagedCourseMessage && (
        <Alert variant="info" className="mb-3">
          No managed courses are available. Assign at least one course to view dispatch logs.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={clearError}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={4}>
              <Form.Group>
                <Form.Label>Course</Form.Label>
                <Form.Select
                  value={courseId}
                  onChange={(event) => setCourseId(event.target.value)}
                  disabled={!userIsAdmin && !scopedCourses.length}
                >
                  {userIsAdmin && <option value="">All Courses</option>}
                  {scopedCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={2}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">All</option>
                  {effectiveStatusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={2}>
              <Form.Group>
                <Form.Label>Template</Form.Label>
                <Form.Select value={templateType} onChange={(event) => setTemplateType(event.target.value)}>
                  <option value="">All</option>
                  {effectiveTemplateTypes.map((option) => (
                    <option key={option} value={option}>
                      {formatTemplateLabel(option)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={2}>
              <Form.Group>
                <Form.Label>Provider</Form.Label>
                <Form.Select value={provider} onChange={(event) => setProvider(event.target.value)}>
                  <option value="">All</option>
                  {effectiveProviderOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={2}>
              <div className="d-grid gap-2">
                <Button variant="outline-secondary" onClick={refresh} disabled={loading || !canQueryLogs}>
                  Refresh
                </Button>
                <Button variant="outline-light" onClick={handleClearFilters}>
                  Clear
                </Button>
              </div>
            </Col>

            <Col lg={12}>
              <Form.Group>
                <Form.Label>Search Recipient/Subject/Error</Form.Label>
                <Form.Control
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by email, subject, or error details"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">Loading email logs...</div>
          ) : !canQueryLogs ? (
            <div className="text-center text-muted py-5">
              Select a managed course to view email logs.
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center text-muted py-5">
              No email logs match the selected filters.
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 170 }}>Timestamp</th>
                  <th>Course</th>
                  <th>Template</th>
                  <th>Recipient</th>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>Subject</th>
                  <th style={{ width: 110 }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="small">{formatDateTime(log.createdAt)}</td>
                    <td>
                      <div>{log.courseTitle || '-'}</div>
                      {log.courseId && (
                        <div className="small text-muted">{log.courseId}</div>
                      )}
                    </td>
                    <td>
                      <div>{formatTemplateLabel(log.templateType)}</div>
                      <div className="small text-muted">{log.templateType}</div>
                    </td>
                    <td>
                      <div>{log.toEmail || '-'}</div>
                      {log.userName && <div className="small text-muted">{log.userName}</div>}
                    </td>
                    <td>
                      <Badge bg={STATUS_VARIANTS[log.status] || 'secondary'}>
                        {log.status}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={PROVIDER_VARIANTS[log.provider] || 'secondary'}>
                        {log.provider || 'unknown'}
                      </Badge>
                    </td>
                    <td className="small" title={log.subject || ''}>
                      {truncate(log.subject || '-', 80)}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setSelectedLog(log)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>

        <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="small text-muted">
            Showing {logs.length} of {pagination.total || 0} result(s)
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={!pagination.hasPreviousPage || loading}
            >
              Previous
            </Button>
            <span className="small text-muted">
              Page {pagination.page || 1} of {pagination.totalPages || 1}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={!pagination.hasNextPage || loading}
            >
              Next
            </Button>
          </div>
        </Card.Footer>
      </Card>

      <Modal
        show={Boolean(selectedLog)}
        onHide={() => setSelectedLog(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Email Dispatch Details</Modal.Title>
        </Modal.Header>

        {selectedLog && (
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <div className="small text-muted">Template</div>
                <div>{formatTemplateLabel(selectedLog.templateType)}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">Timestamp</div>
                <div>{formatDateTime(selectedLog.createdAt)}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">Recipient</div>
                <div>{selectedLog.toEmail || '-'}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">Provider</div>
                <div>{selectedLog.provider || '-'}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">Status</div>
                <div>{selectedLog.status || '-'}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">Provider Message ID</div>
                <div>{selectedLog.providerMessageId || '-'}</div>
              </Col>
              <Col md={12}>
                <div className="small text-muted">Subject</div>
                <div>{selectedLog.subject || '-'}</div>
              </Col>
              <Col md={12}>
                <div className="small text-muted">Body</div>
                <pre className="border rounded p-2 bg-light small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedLog.body || '(empty)'}
                </pre>
              </Col>
              <Col md={12}>
                <div className="small text-muted">Metadata</div>
                <pre className="border rounded p-2 bg-light small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedLog.metadata || {}, null, 2)}
                </pre>
              </Col>
              {selectedLog.errorMessage && (
                <Col md={12}>
                  <Alert variant="danger" className="mb-0">
                    {selectedLog.errorMessage}
                  </Alert>
                </Col>
              )}
            </Row>
          </Modal.Body>
        )}
      </Modal>
    </div>
  );
}
